const getVariantPurchaseState = (option) => {
  const available = option?.dataset?.available === 'true';
  const pricingReviewRequired = option?.dataset?.purchaseBlocked === 'true';
  return {
    available,
    pricingReviewRequired,
    purchasable: available && !pricingReviewRequired
  };
};

const thirdPartyCardPricingState = (card) => {
  const options = [...(card?.querySelectorAll?.('.spf-product__variants option') || [])];
  if (!options.length) return 'unknown';
  let hasUnknownVariant = false;
  for (const option of options) {
    const availability = option.dataset.available;
    const rawPrice = option.dataset.price;
    const price = Number(rawPrice);
    if (!['true', 'false'].includes(availability) || rawPrice == null || rawPrice === '' || !Number.isFinite(price)) {
      hasUnknownVariant = true;
      continue;
    }
    if (availability === 'true' && price <= 0) return 'blocked';
  }
  return hasUnknownVariant ? 'unknown' : 'safe';
};

const thirdPartyCardRequiresPricingReview = (card) => thirdPartyCardPricingState(card) !== 'safe';

const VEHICLE_CONTEXT_STORAGE_KEY = 'avid:vehicle-context';
const VEHICLE_CONTEXT_TTL_MS = 30 * 60 * 1000;
const cleanVehicleContextValue = (value, max = 80) => String(value || '').trim().slice(0, max);
const normalizeVehicleContext = (value) => {
  const model = cleanVehicleContextValue(value?.model || value?.platform);
  const context = {
    year: cleanVehicleContextValue(value?.year, 4),
    make: cleanVehicleContextValue(value?.make),
    platform: model,
    trim: cleanVehicleContextValue(value?.trim, 120),
    ...(value?.applicationId ? {applicationId:cleanVehicleContextValue(value.applicationId,36)} : {}),
    ...(value?.modelId ? {modelId:cleanVehicleContextValue(value.modelId,36)} : {})
  };
  return /^\d{4}$/.test(context.year) && context.make && context.platform ? context : null;
};
const vehicleContextFromUrl = (href, origin = 'https://avidrace.com') => {
  try {
    const params = new URL(href, origin).searchParams;
    return normalizeVehicleContext({
      year: params.get('vehicle_year'),
      make: params.get('vehicle_make'),
      model: params.get('vehicle_model') || params.get('vehicle_platform'),
      trim: params.get('vehicle_trim'),
      applicationId:params.get('vehicle_application_id'), modelId:params.get('vehicle_model_id')
    });
  } catch (_error) {
    return null;
  }
};
const persistVehicleContext = (context, storage, now = Date.now()) => {
  const normalized = normalizeVehicleContext(context);
  if (!normalized || !Number.isFinite(Number(now))) return false;
  try {
    const target = storage || globalThis.sessionStorage;
    target.setItem(VEHICLE_CONTEXT_STORAGE_KEY, JSON.stringify({
      ...normalized,
      expiresAt: Number(now) + VEHICLE_CONTEXT_TTL_MS
    }));
    return true;
  } catch (_error) {
    return false;
  }
};
const readStoredVehicleContext = (storage, now = Date.now()) => {
  try {
    const target = storage || globalThis.sessionStorage;
    const stored = JSON.parse(target.getItem(VEHICLE_CONTEXT_STORAGE_KEY) || 'null');
    const normalized = normalizeVehicleContext(stored);
    if (!normalized || !Number.isFinite(Number(stored?.expiresAt)) || Number(stored.expiresAt) <= Number(now)) {
      target.removeItem(VEHICLE_CONTEXT_STORAGE_KEY);
      return null;
    }
    return normalized;
  } catch (_error) {
    return null;
  }
};
const resolveVehicleContext = ({ href, origin = 'https://avidrace.com', storage, now = Date.now() }) => {
  const urlContext = vehicleContextFromUrl(href, origin);
  if (urlContext) {
    persistVehicleContext(urlContext, storage, now);
    return urlContext;
  }
  return readStoredVehicleContext(storage, now);
};
const vehicleContextLineProperties = (value) => {
  const context = normalizeVehicleContext(value);
  if (!context) return [];
  const label = [context.year, context.make, context.platform].join(' ') + (context.trim ? ` — ${context.trim}` : '');
  return [
    { name: 'properties[Shopping vehicle context]', value: label },
    { name: 'properties[Fitment confirmation]', value: 'Review compatibility and conditions on the product page' },
    ...(context.applicationId ? [
      {name:'properties[_AVID vehicle application]',value:context.applicationId},
      {name:'properties[_AVID vehicle model]',value:context.modelId || ''},
      {name:'properties[_AVID vehicle year]',value:context.year}
    ] : [])
  ];
};
const productVehicleFitmentDecision = ({context,projection}) => {
  const vehicle = globalThis.AvidVehicleSelector?.resolve(context);
  if (projection?.contractVersion !== 1 || !Array.isArray(projection.rules) || !vehicle) return {outcome:'unknown',conditions:[],reasons:['Choose an exact vehicle to check compatibility.']};
  try { return globalThis.AvidCatalogFitment.evaluateProjectedCatalogFitment(projection.rules,vehicle); }
  catch (_) { return {outcome:'unknown',conditions:[],reasons:['Compatibility data needs review.']}; }
};
const productVehicleFitmentState = ({context,projection,requiresVehicleFitment}) => {
  if (!requiresVehicleFitment) return 'not-required';
  if (!normalizeVehicleContext(context) && projection?.contractVersion === 1 && projection.rules?.length) return 'ready';
  const decision=productVehicleFitmentDecision({context,projection});
  return {compatible:'match',conditional:'conditional',incompatible:'mismatch',unknown:'unverified'}[decision.outcome];
};
const readCatalogFitment = host => {
  try { return JSON.parse(host.querySelector('[data-avid-vehicle-fitment], [data-catalog-fitment]')?.textContent || 'null'); }
  catch (_) { return null; }
};
const syncVehicleContextLineProperties = (forms, context) => {
  const properties = vehicleContextLineProperties(context);
  forms.forEach((form) => {
    const existing = [...form.querySelectorAll('[data-avid-vehicle-line-property]')];
    if (!properties.length) {
      existing.forEach((input) => input.remove());
      return;
    }
    existing.filter(input => !properties.some(property => property.name === input.name)).forEach(input => input.remove());
    properties.forEach((property) => {
      let input = existing.find((candidate) => candidate.name === property.name);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = property.name;
        input.dataset.avidVehicleLineProperty = '';
        form.append(input);
      }
      input.value = property.value;
    });
  });
};

const dealerPricingByVariant = new Map();
let dealerPricingUnavailable = false;
let verifiedDealerCartContext = null;
const renderDealerCartContext = () => {
  if (!verifiedDealerCartContext) return;
  document.querySelectorAll('[data-cart-dealer-compact]').forEach((summary) => {
    summary.querySelector('[data-cart-dealer-account-label]').textContent = [verifiedDealerCartContext.accountName, verifiedDealerCartContext.tier].filter(Boolean).join(' - ');
    summary.hidden = false;
    summary.closest('[data-cart-drawer]')?.classList.add('has-dealer');
  });
  document.querySelectorAll('[data-cart-drawer-retail-account]').forEach((panel) => { panel.hidden = true; });
  document.querySelectorAll('[data-cart-dealer-notice]').forEach((notice) => {
    notice.hidden = false;
    notice.querySelector('[data-cart-dealer-name]').textContent = verifiedDealerCartContext.accountName;
    notice.querySelector('[data-cart-dealer-tier]').textContent = verifiedDealerCartContext.tier;
  });
  document.querySelectorAll('[data-cart-rewards-panel]').forEach((panel) => { panel.hidden = true; });
  document.querySelectorAll('[data-cart-assistance], [data-cart-assistance-summary]').forEach((surface) => { surface.dataset.commercialPricingActive = 'true'; });
};
const dealerPriceRangeForVariantIds = (prices, rawIds) => {
  const requestedIds = String(rawIds || '').split(',').map((id) => id.trim()).filter(Boolean);
  if (!requestedIds.length || requestedIds.some((id) => !/^[1-9][0-9]*$/.test(id))) return null;
  const ids = [...new Set(requestedIds)];
  const rows = ids.map((id) => prices.get(id));
  if (rows.some((row) => !row)) return null;
  const currencyCode = rows[0].currencyCode;
  if (rows.some((row) => row.currencyCode !== currencyCode)) return null;
  return {
    currencyCode,
    mapMinimumCents: Math.min(...rows.map((row) => row.mapPriceCents)),
    mapMaximumCents: Math.max(...rows.map((row) => row.mapPriceCents)),
    retailMinimumCents: Math.min(...rows.map((row) => row.retailPriceCents ?? row.mapPriceCents)),
    retailMaximumCents: Math.max(...rows.map((row) => row.retailPriceCents ?? row.mapPriceCents)),
    compareAtMinimumCents: Math.min(...rows.map((row) => row.compareAtPriceCents || 0)),
    compareAtMaximumCents: Math.max(...rows.map((row) => row.compareAtPriceCents || 0)),
    dealerMinimumCents: Math.min(...rows.map((row) => row.dealerPriceCents)),
    dealerMaximumCents: Math.max(...rows.map((row) => row.dealerPriceCents))
  };
};
const formatDealerPriceRange = (minimumCents, maximumCents, currencyCode, fromLabel = 'From', forceFrom = false) => {
  const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode || 'USD' });
  const value = formatter.format(minimumCents / 100);
  return minimumCents === maximumCents && !forceFrom ? value : `${fromLabel} ${value}`;
};
const currencyTextToCents = (value) => {
  const normalized = String(value || '').replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  const amount = Number.parseFloat(normalized);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
};

const updateStorefrontSaleBadge = (root, priceCents, compareAtPriceCents) => {
  let badge = root.querySelector('[data-sale-badge]');
  const price = Number(priceCents), regular = Number(compareAtPriceCents);
  if (!Number.isFinite(price) || !Number.isFinite(regular) || price <= 0 || regular <= price) {
    badge?.remove();
    return;
  }
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'product-price__sale-badge';
    badge.dataset.saleBadge = '';
    root.append(badge);
  }
  const percentage = Math.round((regular - price) * 100 / regular);
  badge.textContent = `${document.documentElement.dataset.saleLabel || 'On Sale'} · ${percentage}% ${document.documentElement.dataset.saleOffLabel || 'off'}`;
};

const createStorefrontPriceElement = ({ variantIds, priceText, priceCents = 0, compareAtPriceText = '', compareAtPriceCents = 0, priceVaries = false, priceMultiplier = 1, priceAdditionCents = 0, className = '' }) => {
  const onSale = Number(compareAtPriceCents) > Number(priceCents) && Number(priceCents) > 0;
  const root = document.createElement('span');
  root.className = ['price', onSale ? 'price--sale' : '', className].filter(Boolean).join(' ');

  const accessibleLabel = document.createElement('span');
  accessibleLabel.className = 'visually-hidden';
  accessibleLabel.textContent = 'Price';
  root.append(accessibleLabel);

  const current = document.createElement('span');
  current.className = `product-price__current${onSale ? ' product-price__current--sale' : ''}`;
  current.textContent = String(priceText || 'See product for price');

  const ids = [...new Set((Array.isArray(variantIds) ? variantIds : [variantIds])
    .map((id) => String(id || '').trim()).filter((id) => /^[1-9][0-9]*$/.test(id)))];
  if (ids.length) {
    root.dataset.dealerPricing = '';
    root.dataset.dealerVariantIds = ids.join(',');
    root.dataset.dealerPriceVaries = String(Boolean(priceVaries));
    root.dataset.dealerPriceMultiplier = String(Math.max(1, Number(priceMultiplier) || 1));
    root.dataset.dealerPriceAdditionCents = String(Math.max(0, Number(priceAdditionCents) || 0));
    root.dataset.mapLabel = document.documentElement.dataset.dealerMapLabel || 'MAP';
    root.dataset.dealerLabel = document.documentElement.dataset.dealerPriceLabel || 'Dealer price';
    root.dataset.fromLabel = document.documentElement.dataset.dealerFromLabel || 'From';

    const mapLabel = document.createElement('span');
    mapLabel.className = 'dealer-price-map-label';
    mapLabel.dataset.dealerMapLabel = '';
    mapLabel.hidden = true;
    mapLabel.textContent = root.dataset.mapLabel;
    root.append(mapLabel);
  }

  root.append(current);

  if (onSale) {
    const compare = document.createElement('s');
    compare.className = 'product-price__compare';
    compare.dataset.publicCompareAt = '';
    const regularLabel = document.createElement('span');
    regularLabel.className = 'visually-hidden';
    regularLabel.textContent = 'Regular price';
    compare.append(regularLabel, document.createTextNode(String(compareAtPriceText || '')));
    root.append(compare);
  }

  if (ids.length) {
    const dealerLine = document.createElement('span');
    dealerLine.className = 'dealer-price-line';
    dealerLine.dataset.dealerPriceLine = '';
    dealerLine.hidden = true;
    const label = document.createElement('span');
    label.textContent = root.dataset.dealerLabel;
    const value = document.createElement('strong');
    value.dataset.dealerPriceValue = '';
    dealerLine.append(label, value);
    root.append(dealerLine);
  }
  updateStorefrontSaleBadge(root, priceCents, compareAtPriceCents);
  return root;
};

if (globalThis.__AVID_THEME_TEST_HOOK__) {
  Object.assign(globalThis.__AVID_THEME_TEST_HOOK__, {
    getVariantPurchaseState,
    thirdPartyCardPricingState,
    thirdPartyCardRequiresPricingReview,
    normalizeVehicleContext,
    vehicleContextFromUrl,
    persistVehicleContext,
    readStoredVehicleContext,
    resolveVehicleContext,
    vehicleContextLineProperties,
    productVehicleFitmentDecision,
    productVehicleFitmentState,
    dealerPriceRangeForVariantIds,
    formatDealerPriceRange,
    createStorefrontPriceElement,
    VEHICLE_CONTEXT_STORAGE_KEY,
    VEHICLE_CONTEXT_TTL_MS
  });
}

class AvidTheme {
  constructor() {
    this.liveRegion = document.querySelector('[data-avid-live-region]');
    this.bindQuantityInputs();
    this.bindTitaniumAnodizingPurchase();
    this.bindProductGallery();
    this.bindProductDetailsAction();
    this.bindVariantSelectors();
    this.bindProductValidation();
    this.bindFacetDrawer();
    this.bindThirdPartyPriceGuards();
    this.bindDealerPriceObserver();
    this.bindJourneyReturnLinks();
    this.bindMobilePurchaseAction();
    this.bindVehicleContextBanner();
    this.bindVehicleContextCartProperties();
    this.bindProductFitmentGuard();
    this.bindCatalogFitmentConsumers();
    this.hydrateDealerPrices();
  }

  announce(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => { this.liveRegion.textContent = message; }, 60);
  }

  async hydrateDealerPrices(root = document) {
    if (dealerPricingUnavailable) return;
    renderDealerCartContext();
    const elements = [];
    if (root instanceof Element && root.matches('[data-dealer-pricing]')) elements.push(root);
    root.querySelectorAll?.('[data-dealer-pricing]').forEach((element) => elements.push(element));
    const pendingElements = elements.filter((element) => element.dataset.dealerPriceHydrated !== element.dataset.dealerVariantIds
      && element.dataset.dealerPriceLoading !== element.dataset.dealerVariantIds);
    if (!pendingElements.length) return;
    pendingElements.forEach((element) => { element.dataset.dealerPriceLoading = element.dataset.dealerVariantIds; });
    const variantIds = [...new Set(pendingElements.flatMap((element) => String(element.dataset.dealerVariantIds || '').split(','))
      .map((id) => id.trim()).filter((id) => /^[1-9][0-9]*$/.test(id)))];
    const missing = variantIds.filter((id) => !dealerPricingByVariant.has(id));
    try {
      for (let index = 0; index < missing.length; index += 100) {
        const batch = missing.slice(index, index + 100);
        const query = new URLSearchParams({ variant_ids: batch.join(',') });
        const response = await fetch(`/apps/avid-commerce/dealer-prices?${query}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Dealer pricing is unavailable.');
        const payload = await response.json();
        if (payload?.dealerPricing !== true) {
          dealerPricingUnavailable = true;
          pendingElements.forEach((element) => { delete element.dataset.dealerPriceLoading; });
          return;
        }
        verifiedDealerCartContext = { accountName: String(payload.accountName || 'Dealer account'), tier: String(payload.tier || 'Approved dealer pricing') };
        renderDealerCartContext();
        Object.entries(payload.prices || {}).forEach(([variantId, row]) => {
          const mapPriceCents = Number(row?.mapPriceCents);
          const retailPriceCents = row?.retailPriceCents == null ? mapPriceCents : Number(row.retailPriceCents);
          const compareAtPriceCents = row?.compareAtPriceCents == null ? 0 : Number(row.compareAtPriceCents);
          const dealerPriceCents = Number(row?.dealerPriceCents);
          const currencyCode = String(row?.currencyCode || 'USD');
          if (/^[1-9][0-9]*$/.test(variantId) && Number.isInteger(mapPriceCents) && Number.isInteger(retailPriceCents)
            && Number.isInteger(compareAtPriceCents) && Number.isInteger(dealerPriceCents) && mapPriceCents >= 0
            && retailPriceCents >= dealerPriceCents && dealerPriceCents >= 0 && compareAtPriceCents >= 0 && /^[A-Z]{3}$/.test(currencyCode)) {
            dealerPricingByVariant.set(variantId, { mapPriceCents, retailPriceCents, compareAtPriceCents, dealerPriceCents, currencyCode });
          }
        });
      }
      pendingElements.forEach((element) => this.renderDealerPrice(element));
    } catch (_error) {
      pendingElements.forEach((element) => element.classList.remove('price--dealer'));
    } finally {
      pendingElements.forEach((element) => { delete element.dataset.dealerPriceLoading; });
    }
  }

  renderDealerPrice(element) {
    if (element.dataset.configurationPricing === 'true') return;
    const cartLines = element.dataset.cartDealerLines;
    const range = dealerPriceRangeForVariantIds(dealerPricingByVariant, cartLines ? cartLines.split(':')[0] : element.dataset.dealerVariantIds);
    const mapLabel = element.querySelector('[data-dealer-map-label]');
    const mapValue = element.querySelector(':scope > .product-price__current');
    const dealerLine = element.querySelector('[data-dealer-price-line]');
    if (!range || !mapLabel || !mapValue || !dealerLine) return;
    const fromLabel = element.dataset.fromLabel || 'From';
    const priceVaries = element.dataset.dealerPriceVaries === 'true';
    const dealerValue = dealerLine.querySelector('[data-dealer-price-value]');
    if (!mapValue || !dealerValue) return;
    const multiplier = Math.max(1, Number(element.dataset.dealerPriceMultiplier) || 1);
    const addition = Math.max(0, Number(element.dataset.dealerPriceAdditionCents) || 0);
    if (element.hasAttribute('data-cart-dealer-pricing')) {
      // Each configured child keeps its own Shopify price and quantity. Variant
      // ranges are for catalog cards; a cart group needs a sum of its exact lines.
      let mapTotal = range.mapMinimumCents * multiplier + addition;
      let expectedTotal = Math.min(range.retailMinimumCents, range.dealerMinimumCents) * multiplier + addition;
      if (element.dataset.cartDealerLines) {
        mapTotal = 0;
        expectedTotal = 0;
        for (const line of element.dataset.cartDealerLines.split(',')) {
          const [variantId, rawQuantity, rawOriginal, rawFinal] = line.split(':');
          const quantity = Number(rawQuantity);
          const price = dealerPricingByVariant.get(variantId);
          if (!Number.isSafeInteger(quantity) || quantity < 1) return;
          if (price) {
            if (price.currencyCode !== range.currencyCode) return;
            mapTotal += price.mapPriceCents * quantity;
            expectedTotal += Math.min(price.retailPriceCents, price.dealerPriceCents) * quantity;
          } else {
            // Service options can be excluded from dealer quotes. Their saved
            // Shopify amounts remain authoritative; never discount them again.
            const original = Number(rawOriginal), final = Number(rawFinal);
            if (element.dataset.cartCurrency !== range.currencyCode || !Number.isSafeInteger(original) || original < 0 || !Number.isSafeInteger(final) || final < 0) return;
            mapTotal += original;
            expectedTotal += final;
          }
        }
      }
      const currency = element.dataset.cartCurrency || range.currencyCode;
      const sameCurrency = currency === range.currencyCode;
      const applied = Number(element.dataset.cartAppliedCents);
      if (!Number.isSafeInteger(applied) || applied < 0) return;
      if (sameCurrency && applied > expectedTotal) {
        // Shopify is charging its regular price because no dealer discount was
        // applied. Keep the Shopify-rendered total visible as the payable price.
        element.dataset.dealerPriceHydrated = element.dataset.dealerVariantIds;
        return;
      }
      mapLabel.textContent = sameCurrency ? 'MAP' : `MAP (${range.currencyCode})`;
      mapLabel.hidden = false;
      mapValue.textContent = formatDealerPriceRange(mapTotal, mapTotal, range.currencyCode);
      mapValue.hidden = false;
      dealerLine.querySelector('[data-cart-dealer-label]').textContent = sameCurrency ? 'Dealer Price' : `Dealer Price (${currency})`;
      dealerValue.textContent = formatDealerPriceRange(applied, applied, currency);
      dealerLine.hidden = false;
      const status = element.querySelector('[data-cart-dealer-status]');
      const mismatch = sameCurrency && applied !== expectedTotal;
      status.hidden = !mismatch;
      status.textContent = mismatch ? 'Your dealer price has not been applied. Refresh the cart before checkout.' : '';
      element.parentElement.querySelectorAll('[data-cart-retail-pricing]').forEach((node) => { node.hidden = true; });
      element.classList.add('price--dealer');
      element.dataset.dealerPriceHydrated = element.dataset.dealerVariantIds;
      return;
    }
    const retailMinimum = range.retailMinimumCents * multiplier + addition;
    const retailMaximum = range.retailMaximumCents * multiplier + addition;
    const compareMinimum = range.compareAtMinimumCents * multiplier + addition;
    const compareMaximum = range.compareAtMaximumCents * multiplier + addition;
    const dealerMinimum = range.dealerMinimumCents * multiplier + addition;
    const dealerMaximum = range.dealerMaximumCents * multiplier + addition;
    const onSale = compareMaximum > retailMaximum && compareMinimum > retailMinimum;
    mapValue.textContent = formatDealerPriceRange(retailMinimum, retailMaximum, range.currencyCode, fromLabel, priceVaries);
    mapValue.classList.toggle('product-price__current--sale', onSale);
    let compare = element.querySelector('[data-public-compare-at]');
    if (onSale && !compare) {
      compare = document.createElement('s');
      compare.className = 'product-price__compare';
      compare.dataset.publicCompareAt = '';
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = 'Regular price';
      compare.append(label, document.createTextNode(''));
      mapValue.after(compare);
    }
    if (compare) {
      if (onSale) {
        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = 'Regular price';
        compare.replaceChildren(label, document.createTextNode(formatDealerPriceRange(compareMinimum, compareMaximum, range.currencyCode, fromLabel, priceVaries)));
      }
      compare.hidden = !onSale;
    }
    element.classList.toggle('price--sale', onSale);
    updateStorefrontSaleBadge(element, retailMinimum, onSale ? compareMinimum : 0);
    dealerValue.textContent = formatDealerPriceRange(range.dealerMinimumCents * multiplier + addition, range.dealerMaximumCents * multiplier + addition, range.currencyCode, fromLabel, priceVaries);
    const dealerIsLower = dealerMinimum < retailMinimum || dealerMaximum < retailMaximum;
    mapLabel.hidden = onSale || !dealerIsLower;
    dealerLine.hidden = !dealerIsLower;
    element.classList.toggle('price--dealer', dealerIsLower);
    element.dataset.dealerPriceHydrated = element.dataset.dealerVariantIds;

    const productRoot = element.closest('[data-product-root]');
    const form = productRoot?.querySelector('[data-product-form]');
    const variantControl = form?.querySelector('[name="id"]');
    const selectedOption = variantControl?.tagName === 'SELECT' ? variantControl.selectedOptions?.[0] : null;
    const selectedVariantId = selectedOption?.value || variantControl?.value || '';
    const selectedPrice = dealerPricingByVariant.get(selectedVariantId);
    if (!selectedPrice) return;
    if (selectedOption) selectedOption.dataset.dealerPriceCents = String(selectedPrice.dealerPriceCents);
    const totalRoot = form?.querySelector('[data-product-running-total]');
    const quantity = Math.max(1, Number.parseInt(form?.querySelector('[name="quantity"]')?.value, 10) || 1);
    if (totalRoot) {
      totalRoot.dataset.dealerPriceCents = String(selectedPrice.dealerPriceCents);
      const totalValue = totalRoot.querySelector('[data-product-running-total-value]');
      if (totalValue) totalValue.textContent = formatDealerPriceRange(selectedPrice.dealerPriceCents * quantity, selectedPrice.dealerPriceCents * quantity, selectedPrice.currencyCode, fromLabel);
    }
    const mobilePrice = productRoot?.querySelector('[data-mobile-purchase-price]');
    if (mobilePrice) mobilePrice.textContent = formatDealerPriceRange(selectedPrice.dealerPriceCents, selectedPrice.dealerPriceCents, selectedPrice.currencyCode, fromLabel);
  }

  bindDealerPriceObserver() {
    if (!globalThis.MutationObserver || document.documentElement.dataset.avidDealerPriceObserverReady === 'true') return;
    document.documentElement.dataset.avidDealerPriceObserverReady = 'true';
    let scheduled = false;
    const observer = new MutationObserver((mutations) => {
      const hasDealerPrice = mutations.some((mutation) => [...mutation.addedNodes].some((node) => node instanceof Element
        && (node.matches('[data-dealer-pricing]') || node.querySelector?.('[data-dealer-pricing]'))));
      if (!hasDealerPrice || scheduled) return;
      scheduled = true;
      Promise.resolve().then(() => {
        scheduled = false;
        void this.hydrateDealerPrices(document);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  bindQuantityInputs() {
    document.querySelectorAll('[data-quantity]').forEach((wrapper) => {
      if (wrapper.dataset.avidQuantityReady === 'true') return;
      wrapper.dataset.avidQuantityReady = 'true';
      const input = wrapper.querySelector('input');
      if (!input) return;
      wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) - 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        input.value = String(Math.min(Number(input.max || 9999), Number(input.value || 1) + 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  bindTitaniumAnodizingPurchase() {
    document.querySelectorAll('[data-titanium-anodizing-purchase]').forEach((root) => {
      const form = root.querySelector('[data-product-form]');
      const controls = form?.querySelector('[data-titanium-anodizing]');
      if (!form || !controls || form.dataset.avidTitaniumPurchaseReady === 'true') return;
      form.dataset.avidTitaniumPurchaseReady = 'true';
      const finishInputs = [...controls.querySelectorAll('[data-titanium-finish-option] input[type="radio"]')];
      const status = controls.querySelector('[data-titanium-anodizing-status]');
      const configurator = form.querySelector('[data-avid-configurator]');
      const totalRoot = form.querySelector('[data-product-running-total]');
      const totalValue = totalRoot?.querySelector('[data-product-running-total-value]');
      const totalNote = totalRoot?.querySelector('[data-product-running-total-note]');
      const submit = form.querySelector('[name="add"]');
      const selectedOption = () => finishInputs.find((input) => input.checked)?.closest('[data-titanium-finish-option]') || null;
      const configuredReleaseVisible = () => Boolean(configurator && !configurator.hidden && configurator.querySelector('[data-avid-configurator-groups]')?.children.length);
      const syncVisibility = () => { controls.hidden = configuredReleaseVisible(); };
      const refresh = () => {
        syncVisibility();
        const option = selectedOption();
        const label = option?.dataset.finishLabel || 'Raw Ti';
        const quantity = Math.max(1, Number.parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1);
        const piecesPerUnit = Math.max(0, Number.parseInt(controls.dataset.anodizablePieceCount, 10) || 0);
        const totalPieces = piecesPerUnit * quantity;
        const minimum = Number(controls.dataset.anodizingMinimumCents || 500);
        const unit = Number(controls.dataset.anodizingUnitCents || 37);
        const charge = totalPieces > 0 ? Math.max(minimum, unit * totalPieces) : 0;
        const variantControl = form.querySelector('[name="id"]');
        const selectedVariantOption = variantControl?.tagName === 'SELECT' ? variantControl.selectedOptions?.[0] : null;
        const basePrice = Number(selectedVariantOption?.dataset.dealerPriceCents || totalRoot?.dataset.dealerPriceCents || selectedVariantOption?.dataset.priceCents || totalRoot?.dataset.basePriceCents || 0);
        const totalCents = (basePrice * quantity) + (label === 'Raw Ti' ? 0 : charge);
        const chargeLabel = new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(charge / 100);
        const totalLabel = new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(totalCents / 100);
        const feeReady = controls.dataset.anodizingFeeReady === 'true'
          && controls.dataset.anodizingMinimumVariantId
          && controls.dataset.anodizingPieceVariantId;
        const finishReady = label === 'Raw Ti' || Boolean(totalPieces && feeReady);
        if (totalValue) totalValue.textContent = totalLabel;
        if (totalNote) totalNote.textContent = label === 'Raw Ti'
          ? `${quantity} × current product price`
          : totalPieces
            ? `Estimate with anodizing. Same-color pieces combine in the cart; the minimum applies once per color.`
            : 'Anodizable-piece count required';
        if (status) {
          if (label === 'Raw Ti') status.textContent = 'Raw Ti selected. No anodizing charge.';
          else if (!totalPieces) status.textContent = `${label} selected. The reviewed anodizable-piece count is missing, so checkout is blocked.`;
          else if (!feeReady) status.textContent = `${label} selected. ${totalPieces} anodizable ${totalPieces === 1 ? 'piece' : 'pieces'} = ${chargeLabel}. The governed fee service is not available.`;
          else status.textContent = `${label} selected. ${totalPieces} anodizable ${totalPieces === 1 ? 'piece' : 'pieces'}. Estimated service: ${chargeLabel}. Same-color items combine in the cart with one minimum per color.`;
        }
        if (submit && !submit.dataset.avidConfigurationDisabled && !submit.dataset.avidInventoryDisabled && !submit.dataset.avidPricingDisabled) {
          submit.disabled = !(option && option.dataset.finishAvailable === 'true' && finishReady);
        }
        return Boolean(option && option.dataset.finishAvailable === 'true' && finishReady);
      };
      finishInputs.forEach((input) => input.addEventListener('change', refresh));
      form.querySelector('[name="quantity"]')?.addEventListener('input', refresh);
      form.querySelector('[name="quantity"]')?.addEventListener('change', refresh);
      form.querySelector('[name="id"]')?.addEventListener('change', refresh);
      if (configurator && globalThis.MutationObserver) new MutationObserver(syncVisibility).observe(configurator, { attributes: true, childList: true, subtree: true });
      form.addEventListener('submit', async (event) => {
        if (configuredReleaseVisible()) return;
        const option = selectedOption();
        const label = option?.dataset.finishLabel || 'Raw Ti';
        if (label === 'Raw Ti') return;
        event.preventDefault();
        event.stopPropagation();
        if (!refresh()) {
          option?.querySelector('input')?.focus();
          return;
        }
        const quantity = Math.max(1, Number.parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1);
        const piecesPerUnit = Math.max(0, Number.parseInt(controls.dataset.anodizablePieceCount, 10) || 0);
        const sourceVariantId = form.querySelector('[name="id"]')?.value;
        const groupId = `avid-ti-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const finishImage = option?.dataset.finishImage || '';
        if (!sourceVariantId) return;
        if (submit) submit.disabled = true;
        let purchaseError = '';
        try {
          if (!window.avidCart?.addTitaniumItem) throw new Error('The cart is still loading. Please try again.');
          const properties = Object.fromEntries([...new FormData(form)].filter(([name]) => /^properties\[.+\]$/.test(name)).map(([name, value]) => [name.slice(11, -1), value]));
          await window.avidCart.addTitaniumItem({ id: sourceVariantId, quantity, properties: {
            ...properties, 'Titanium finish': label, '_AVID anodizable pieces per unit': String(piecesPerUnit),
            '_AVID Titanium group': groupId, '_AVID Titanium selected finish image': finishImage
          } }, { open: true, trigger: submit, announcement: `${label} Titanium selection added to cart.` });
        } catch (error) {
          purchaseError = error?.message || 'The Titanium product and anodizing charge could not be added.';
          option?.querySelector('input')?.focus();
        } finally {
          if (submit) submit.disabled = false;
          refresh();
          if (status && purchaseError) status.textContent = purchaseError;
        }
      });
      refresh();
    });
  }

  bindMobilePurchaseAction() {
    document.querySelectorAll('[data-mobile-purchase-action]').forEach((button) => {
      if (button.dataset.avidMobilePurchaseReady === 'true') return;
      button.dataset.avidMobilePurchaseReady = 'true';
      const productRoot = button.closest('[data-product-root]');
      const targetId = button.getAttribute('aria-controls');
      const heading = targetId ? document.getElementById(targetId) : null;
      const purchaseControl = heading?.closest('.product-purchase-control');
      const form = purchaseControl?.querySelector('[data-product-form]');
      const submit = form?.querySelector('[name="add"]');
      const price = button.querySelector('[data-mobile-purchase-price]');
      const label = button.querySelector('[data-mobile-purchase-label]');
      if (!purchaseControl || !form || !submit) return;

      const sync = () => {
        const pricingReview = submit.dataset.avidPricingDisabled === 'true';
        const soldOut = submit.dataset.avidInventoryDisabled === 'true';
        const configurationRequired = submit.dataset.avidConfigurationDisabled === 'true';
        const currentPrice = productRoot?.querySelector('[data-product-running-total-value]')?.textContent?.trim()
          || productRoot?.querySelector('[data-product-price]:not([hidden]) .product-price__current')?.textContent?.trim()
          || '';
        const state = pricingReview ? 'pricing-review'
          : soldOut ? 'sold-out'
            : configurationRequired ? 'configuration'
              : 'available';
        const actionLabel = pricingReview ? button.dataset.pricingReviewLabel
          : soldOut ? button.dataset.soldOutLabel
            : configurationRequired ? button.dataset.configurationLabel
              : button.dataset.availableLabel;
        button.dataset.purchaseState = state;
        if (price) price.textContent = pricingReview ? 'Pricing review' : currentPrice || 'Review options';
        if (label) label.textContent = actionLabel || 'Review purchase options';
        button.setAttribute('aria-label', `${actionLabel || 'Review purchase options'}${pricingReview ? '' : currentPrice ? `, ${currentPrice}` : ''}. Opens purchase options.`);
      };

      button.addEventListener('click', () => {
        const top = purchaseControl.getBoundingClientRect().top + window.scrollY - 88;
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
        const focusTarget = submit.dataset.avidPricingDisabled === 'true'
            ? purchaseControl.querySelector('[data-product-pricing-review] a')
            : purchaseControl.querySelector('select:not([disabled]), input[type="radio"]:checked:not([disabled]), input[name="quantity"]:not([disabled]), button:not([disabled]), a[href]');
        window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 260);
        this.announce('Purchase options are ready.');
      });

      productRoot?.addEventListener('avid:variant-change', sync);
      document.addEventListener('avid:vehicle-context-change', sync);
      if (globalThis.MutationObserver) {
        new MutationObserver(sync).observe(purchaseControl, {
          attributes: true,
          attributeFilter: ['disabled', 'data-avid-inventory-disabled', 'data-avid-pricing-disabled', 'data-avid-configuration-disabled'],
          childList: true,
          characterData: true,
          subtree: true
        });
      }
      if (globalThis.IntersectionObserver) {
        new IntersectionObserver(([entry]) => {
          button.hidden = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.35);
        }, { threshold: [0.35] }).observe(purchaseControl);
      }
      sync();
    });
  }

  bindProductDetailsAction() {
    document.querySelectorAll('[data-product-details-action]').forEach((button) => {
      if (button.dataset.avidProductDetailsReady === 'true') return;
      button.dataset.avidProductDetailsReady = 'true';
      button.addEventListener('click', () => {
        const targetId = button.getAttribute('aria-controls');
        const target = targetId ? document.getElementById(targetId) : null;
        if (!target) return;
        const top = target.getBoundingClientRect().top + window.scrollY - 110;
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' });
        window.setTimeout(() => target.querySelector('summary')?.focus({ preventScroll: true }), 260);
        this.announce('Product details are ready.');
      });
    });
  }

  bindProductGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.avidGalleryReady === 'true') return;
      gallery.dataset.avidGalleryReady = 'true';
      const items = [...gallery.querySelectorAll('[data-media-id]')];
      gallery.querySelectorAll('[data-media-target]').forEach((button) => {
        button.addEventListener('click', () => {
          const id = button.dataset.mediaTarget;
          items.forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === id));
          gallery.querySelectorAll('[data-media-target]').forEach((thumb) => thumb.setAttribute('aria-current', String(thumb === button)));
        });
      });
    });
  }

  bindVariantSelectors() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.avidVariantReady === 'true') return;
      form.dataset.avidVariantReady = 'true';
      const select = form.querySelector('[data-variant-select]');
      const submit = form.querySelector('[name="add"]');
      const productRoot = form.closest('[data-product-root]');
      const price = productRoot?.querySelector('[data-product-price]');
      const status = productRoot?.querySelector('[data-product-status]');
      const leadTime = productRoot?.querySelector('[data-product-lead-time]');
      const pricingReview = productRoot?.querySelector('[data-product-pricing-review]');
      const sku = productRoot?.querySelector('[data-variant-sku]');
      if (!select) return;
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const { available, pricingReviewRequired, purchasable } = getVariantPurchaseState(option);
        if (price && option.dataset.price) {
          const template = document.createElement('template');
          template.innerHTML = option.dataset.price;
          const nextCurrent = template.content.querySelector('.product-price__current');
          const current = price.querySelector('.price > .product-price__current');
          if (nextCurrent && current) {
            current.textContent = nextCurrent.textContent;
            current.className = nextCurrent.className;
          }

          const nextCompare = template.content.querySelector('.product-price__compare');
          const compare = price.querySelector('.price > .product-price__compare');
          if (nextCompare && compare) compare.replaceWith(nextCompare.cloneNode(true));
          else if (nextCompare && current) current.after(nextCompare.cloneNode(true));
          else if (!nextCompare && compare) compare.remove();

          const nextBadge = template.content.querySelector('[data-sale-badge]');
          const badge = price.querySelector('[data-sale-badge]');
          if (nextBadge && badge) badge.replaceWith(nextBadge.cloneNode(true));
          else if (nextBadge && price.querySelector('.price')) price.querySelector('.price').append(nextBadge.cloneNode(true));
          else if (!nextBadge && badge) badge.remove();
          price.querySelector('.price')?.classList.toggle('price--sale', Boolean(nextCompare));

          const nextUnit = template.content.querySelector('.product-price__unit');
          const unit = price.querySelector('.price > .product-price__unit');
          if (nextUnit && unit) unit.replaceWith(nextUnit.cloneNode(true));
          else if (nextUnit && price.querySelector('.price')) price.querySelector('.price').append(nextUnit.cloneNode(true));
          else if (!nextUnit && unit) unit.remove();

          price.hidden = pricingReviewRequired;
          const dealerPrice = price.querySelector('[data-dealer-pricing]');
          if (dealerPrice) {
            dealerPrice.dataset.dealerVariantIds = select.value;
            dealerPrice.dataset.dealerPriceVaries = 'false';
            dealerPrice.classList.remove('price--dealer');
            const dealerMapLabel = dealerPrice.querySelector('[data-dealer-map-label]');
            const dealerLine = dealerPrice.querySelector('[data-dealer-price-line]');
            if (dealerMapLabel) dealerMapLabel.hidden = true;
            if (dealerLine) dealerLine.hidden = true;
            this.hydrateDealerPrices(dealerPrice);
          }
        }
        if (submit) {
          const themeOwnedDisabled = submit.dataset.avidInventoryDisabled === 'true' || submit.dataset.avidPricingDisabled === 'true' || submit.dataset.avidConfigurationDisabled === 'true';
          const externallyDisabled = submit.disabled && !themeOwnedDisabled;
          if (!available) {
            submit.dataset.avidInventoryDisabled = 'true';
          } else {
            delete submit.dataset.avidInventoryDisabled;
          }

          if (pricingReviewRequired) {
            submit.dataset.avidPricingDisabled = 'true';
          } else {
            delete submit.dataset.avidPricingDisabled;
          }

          const configurationBlocked = submit.dataset.avidConfigurationDisabled === 'true';
          submit.disabled = externallyDisabled || !purchasable || configurationBlocked;
          const submitLabel = submit.querySelector('span');
          if (submitLabel) {
            if (pricingReviewRequired) submitLabel.textContent = submit.dataset.pricingReviewLabel;
            else if (!available) submitLabel.textContent = submit.dataset.soldOutLabel;
            else if (configurationBlocked) submitLabel.textContent = submit.dataset.previewLabel || 'Save configuration';
            else submitLabel.textContent = submit.dataset.availableLabel;
          }
        }
        form.dataset.purchaseBlocked = String(pricingReviewRequired);
        if (status) {
          if (pricingReviewRequired) status.textContent = status.dataset.pricingReviewCopy;
          else status.textContent = available ? status.dataset.availableCopy : status.dataset.soldOutCopy;
          status.classList.toggle('product-status--out', !available);
          status.classList.toggle('product-status--review', pricingReviewRequired);
        }
        if (leadTime) leadTime.hidden = !purchasable;
        if (pricingReview) {
          pricingReview.hidden = !pricingReviewRequired;
          // Inactive warnings must not become product facts in page readers.
          pricingReview.replaceChildren();
          if (pricingReviewRequired) {
            const title = document.createElement('strong');
            title.textContent = 'This product is not available to purchase at the displayed price.';
            const message = document.createElement('p');
            message.textContent = 'AVID must confirm a valid selling price before this variant can be added to cart. Contact us for current pricing and availability.';
            const link = document.createElement('a');
            link.href = pricingReview.dataset.supportUrl;
            link.textContent = 'Request pricing review';
            pricingReview.append(title, message, link);
          }
        }
        if (sku) {
          const variantSku = option.dataset.sku || '';
          sku.textContent = variantSku ? `SKU ${variantSku}` : '';
          sku.hidden = !variantSku;
        }
        if (option.dataset.mediaId) {
          productRoot?.querySelector(`[data-media-target="${CSS.escape(option.dataset.mediaId)}"]`)?.click();
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', select.value);
        window.history.replaceState({}, '', url);
        productRoot?.dispatchEvent(new CustomEvent('avid:variant-change', {
          bubbles: true,
          detail: {
            variantId: select.value,
            available,
            pricingReviewRequired,
            purchasable,
            priceCents: Number(option.dataset.priceCents || 0)
          }
        }));
        const availabilityAnnouncement = pricingReviewRequired
          ? status?.dataset.pricingReviewCopy || 'Pricing review required'
          : available
            ? status?.dataset.availableCopy || 'Available'
            : status?.dataset.soldOutCopy || 'Sold out';
        this.announce(`${option.textContent.trim()}. ${pricingReviewRequired ? '' : option.dataset.priceText || ''}. ${availabilityAnnouncement}`);
      });
    });
  }

  bindProductValidation() {
    if (document.documentElement.dataset.avidProductValidationReady === 'true') return;
    document.documentElement.dataset.avidProductValidationReady = 'true';

    const clearOptionError = (form) => {
      const productRoot = form.closest('[data-product-root]') || form;
      productRoot.querySelectorAll('.avp-option[aria-invalid="true"]').forEach((option) => option.removeAttribute('aria-invalid'));
      form.querySelector('[data-product-option-error]')?.remove();
    };

    const validateRequiredOptions = (form, event) => {
      const productRoot = form.closest('[data-product-root]') || form;
      clearOptionError(form);
      if (form.dataset.purchaseBlocked === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        const pricingError = document.createElement('p');
        pricingError.className = 'product-form__option-error';
        pricingError.dataset.productOptionError = '';
        pricingError.setAttribute('role', 'alert');
        pricingError.setAttribute('tabindex', '-1');
        pricingError.textContent = 'This product cannot be added to cart until AVID confirms a valid selling price.';
        const insertionPoint = form.querySelector('.product-form__required-note, .buy-buttons');
        if (insertionPoint) insertionPoint.before(pricingError);
        else form.append(pricingError);
        pricingError.focus({ preventScroll: true });
        this.announce(pricingError.textContent);
        return false;
      }
      const requiredOptions = [...productRoot.querySelectorAll('.avp-option')].filter((option) => (
        option.querySelector('.avp-required, [apo-required="true"], input[required], select[required], textarea[required]')
      ));

      const invalidOption = requiredOptions.find((option) => {
        const radios = [...option.querySelectorAll('input[type="radio"]:not(:disabled)')];
        if (radios.length > 0) return !radios.some((input) => input.checked);

        const checkboxes = [...option.querySelectorAll('input[type="checkbox"]:not(:disabled)')];
        if (checkboxes.length > 0 && option.querySelector('[apo-required="true"]')) {
          return !checkboxes.some((input) => input.checked);
        }

        const requiredFields = [...option.querySelectorAll('input[required], select[required], textarea[required]')]
          .filter((field) => !field.disabled && field.type !== 'hidden');
        return requiredFields.some((field) => !String(field.value || '').trim());
      });

      if (!invalidOption) return true;
      event.preventDefault();
      event.stopImmediatePropagation();
      invalidOption.setAttribute('aria-invalid', 'true');

      const error = document.createElement('p');
      error.className = 'product-form__option-error';
      error.dataset.productOptionError = '';
      error.setAttribute('role', 'alert');
      error.setAttribute('tabindex', '-1');
      error.textContent = 'Complete every required product option before adding this item to your cart.';
      const insertionPoint = form.querySelector('.product-form__required-note, .buy-buttons');
      if (insertionPoint) insertionPoint.before(error);
      else form.append(error);
      error.focus({ preventScroll: true });
      this.announce(error.textContent);
      return false;
    };

    document.addEventListener('input', (event) => {
      const form = event.target.closest('[data-product-form]');
      if (form) clearOptionError(form);
    }, true);
    document.addEventListener('change', (event) => {
      const form = event.target.closest('[data-product-form]');
      if (form) clearOptionError(form);
    }, true);
    document.addEventListener('click', (event) => {
      const submit = event.target.closest('[data-product-form] [name="add"]');
      if (submit) validateRequiredOptions(submit.closest('[data-product-form]'), event);
    }, true);
    document.addEventListener('submit', (event) => {
      if (event.target.matches('[data-product-form]')) validateRequiredOptions(event.target, event);
    }, true);
  }

  bindFacetDrawer() {
    document.querySelectorAll('[data-facets-trigger]').forEach((trigger) => {
      if (trigger.dataset.avidFacetsReady === 'true') return;
      trigger.dataset.avidFacetsReady = 'true';
      const form = document.querySelector(trigger.dataset.facetsTrigger);
      if (!form) return;
      trigger.addEventListener('click', () => {
        const open = form.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', String(open));
      });
    });
  }

  bindJourneyReturnLinks() {
    const links = [...document.querySelectorAll('[data-journey-return-link="shock"]')];
    if (!links.length) return;
    let stored;
    try {
      stored = JSON.parse(sessionStorage.getItem('avid:storefront-return') || 'null');
    } catch (_error) {
      return;
    }
    if (!stored || stored.family !== 'shock' || Number(stored.expiresAt) < Date.now()) return;

    try {
      const base = new URL(links[0].href, window.location.origin);
      const candidate = new URL(stored.url, window.location.origin);
      if (candidate.origin !== window.location.origin || candidate.pathname !== base.pathname) return;

      const safe = new URL(base.pathname, window.location.origin);
      if (base.searchParams.get('view') === 'shock-center') safe.searchParams.set('view', 'shock-center');
      candidate.searchParams.forEach((value, key) => {
        if (key.startsWith('shock_')) safe.searchParams.set(key, value);
      });
      links.forEach((link) => { link.href = `${safe.pathname}${safe.search}`; });
    } catch (_error) {
      // A stale return hint must never interfere with the product page.
    }
  }

  bindVehicleContextBanner() {
    const host = document.querySelector('[data-product-root], .catalog-collection, [data-shock-center], [data-titanium-center], [data-suspension-center], [data-fabrication-center]');
    if (!host) return;

    const render = () => {
    host.querySelector('[data-vehicle-context-banner]')?.remove();
    const context = resolveVehicleContext({ href: window.location.href, origin: window.location.origin });
    if (!context) return;

    const banner = document.createElement('aside');
    banner.className = 'vehicle-context-banner page-width';
    banner.dataset.vehicleContextBanner = '';
    banner.setAttribute('aria-label', 'Selected vehicle shopping context');
    const copy = document.createElement('div');
    const label = [context.year, context.make, context.platform].join(' ') + (context.trim ? ` — ${context.trim}` : '');
    const eyebrow = document.createElement('span');
    eyebrow.textContent = 'Selected vehicle context';
    const strong = document.createElement('strong');
    strong.textContent = label;
    const note = document.createElement('small');
    note.textContent = 'This vehicle is retained for comparison only. Confirm the application and conditions on every product before ordering.';
    copy.append(eyebrow, strong, note);
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.textContent = 'Clear vehicle';
    clear.addEventListener('click', () => {
      globalThis.AvidVehicleSelector.clear();
    });
    banner.append(copy, clear);
    host.prepend(banner);
    };
    render();
    document.addEventListener('avid:vehicle-context-change', render);
  }

  bindVehicleContextCartProperties() {
    const forms = [...document.querySelectorAll('[data-product-form], [data-contextual-product-form]')];
    if (!forms.length) return;

    const context = resolveVehicleContext({ href: window.location.href, origin: window.location.origin });
    syncVehicleContextLineProperties(forms, context);
    document.addEventListener('avid:vehicle-context-change', (event) => {
      syncVehicleContextLineProperties(forms, event.detail?.context || null);
    });
  }

  bindProductFitmentGuard() {
    const productRoot = document.querySelector('[data-product-root]');
    if (!productRoot) return;
    const forms = [...productRoot.querySelectorAll('[data-product-form]')];
    if (!forms.length) return;

    const review = productRoot.querySelector('[data-product-fitment-review]');
    const reviewTitle = review?.querySelector('[data-product-fitment-review-title]');
    const reviewCopy = review?.querySelector('[data-product-fitment-review-copy]');
    const confirmation = productRoot.querySelector('[data-product-fitment-confirmation]');
    const projection = readCatalogFitment(productRoot);
    const sync = context => {
      const decision = productVehicleFitmentDecision({context,projection});
      const state = productVehicleFitmentState({context,projection,requiresVehicleFitment:productRoot.dataset.requiresVehicleFitment === 'true'});
      const warning = ['mismatch','unverified','conditional'].includes(state);
      forms.forEach(form => { form.dataset.fitmentWarning=String(warning); form.dataset.fitmentState=state; });
      if (review) {
        review.hidden=!warning;
        review.classList.toggle('product-fitment-guidance--mismatch',state==='mismatch');
        if (reviewTitle && reviewCopy && warning) {
          reviewTitle.textContent = state==='mismatch'?'This configuration is excluded.':state==='conditional'?'Compatibility requires these conditions.':'Confirm fitment before ordering.';
          reviewCopy.textContent = state==='conditional' ? decision.conditions.join(' · ') || 'Contact AVID to confirm the required installation conditions.'
            : state==='mismatch' ? 'The selected vehicle matches an excluded configuration. Choose a different product or contact AVID.'
            : 'Choose your exact vehicle to check the published rules. If your configuration is not confirmed, contact AVID before ordering.';
        }
      }
      if (confirmation) confirmation.hidden=warning;
    };

    sync(resolveVehicleContext({ href: window.location.href, origin: window.location.origin }));
    document.addEventListener('avid:vehicle-context-change', (event) => sync(event.detail?.context || null));
  }

  bindCatalogFitmentConsumers() {
    const update = () => {
      const selected=resolveVehicleContext({href:window.location.href,origin:window.location.origin});
      document.querySelectorAll('[data-product-card], [data-cart-line]').forEach(host=>{
        const projection=readCatalogFitment(host);
        const isCart=host.hasAttribute('data-cart-line');
        if (!projection?.rules?.length && !projection?.reviewRequired && (!isCart || host.dataset.cartFitmentState==='not-required')) return;
        const context=isCart ? {applicationId:host.dataset.vehicleApplicationId,modelId:host.dataset.vehicleModelId,year:host.dataset.vehicleYear} : selected;
        const decision=productVehicleFitmentDecision({context,projection});
        const label={compatible:'Compatible with the selected vehicle.',conditional:'Conditional fitment. Review installation requirements.',incompatible:'The selected configuration is excluded.',unknown:projection?.reviewRequired?'Needs fitment review. Confirm exact compatibility before ordering.':'Confirm exact vehicle compatibility on the product page.'}[decision.outcome];
        const status=host.querySelector(isCart?'[data-cart-fitment-guidance]':'[data-catalog-card-fitment]');
        if (status) {status.textContent=label;status.hidden=false;}
        if (isCart) host.dataset.cartFitmentState={compatible:'ready',conditional:'conditional',incompatible:'mismatch',unknown:'unverified'}[decision.outcome];
      });
    };
    update();
    if(document.documentElement.dataset.catalogFitmentConsumersReady==='true')return;
    document.documentElement.dataset.catalogFitmentConsumersReady='true';
    document.addEventListener('cart:updated',update);
    document.addEventListener('avid:vehicle-context-change',update);
  }

  bindThirdPartyPriceGuards() {
    const prepareDealerPrice = (card) => {
      const priceWrapper = card.querySelector('.spf-product-card__price-wrapper');
      if (!priceWrapper || priceWrapper.dataset.avidDealerPrice === 'true') return;
      const options = [...card.querySelectorAll('.spf-product__variants option')]
        .filter((option) => option.dataset.available === 'true' && Number(option.dataset.price) > 0);
      const displayOption = options.reduce((lowest, option) => Number(option.dataset.price) < Number(lowest.dataset.price) ? option : lowest, options[0]);
      const variantIds = [displayOption?.value || displayOption?.dataset.variantId].filter(Boolean);
      if (!variantIds.length) return;
      const currentPriceNode = priceWrapper.querySelector('.spf-product-card__saleprice, .spf-product-card__price, [data-price]');
      const comparePriceNode = priceWrapper.querySelector('.spf-product-card__oldprice, s, del');
      const priceText = currentPriceNode?.textContent.trim() || priceWrapper.textContent.trim();
      const priceVaries = new Set(options.map((option) => Number(option.dataset.price))).size > 1;
      const priceCents = Number(displayOption.dataset.price);
      const compareAtPriceCents = Number(displayOption.dataset.compareAtPrice || 0) || currencyTextToCents(comparePriceNode?.textContent);
      const compareAtPriceText = displayOption.dataset.compareAtPriceText || comparePriceNode?.textContent.trim() || '';
      const price = createStorefrontPriceElement({ variantIds, priceText, priceCents, compareAtPriceCents, compareAtPriceText, priceVaries });
      priceWrapper.replaceChildren(price);
      priceWrapper.dataset.avidDealerPrice = 'true';
      void this.hydrateDealerPrices(price);
    };

    const guardCard = (card, { failClosed = false } = {}) => {
      if (!(card instanceof Element) || !card.matches('.spf-product-card')) return;
      const pricingState = thirdPartyCardPricingState(card);
      if (pricingState === 'safe') {
        prepareDealerPrice(card);
        return;
      }
      if (pricingState === 'unknown' && !failClosed) return;

      card.dataset.avidPricingReview = 'true';
      card.classList.add('spf-product-card--pricing-review');
      card.querySelectorAll('.open-quick-view, .spf-product__form button, .spf-product__form [type="submit"]').forEach((control) => {
        control.setAttribute('aria-disabled', 'true');
        if ('disabled' in control) control.disabled = true;
        if (control.classList.contains('open-quick-view')) {
          control.removeAttribute('onclick');
          control.removeAttribute('href');
          control.setAttribute('hidden', '');
        }
      });

      const form = card.querySelector('.spf-product__form');
      if (form) {
        form.dataset.purchaseBlocked = 'true';
        form.removeAttribute('action');
      }

      const priceWrapper = card.querySelector('.spf-product-card__price-wrapper');
      if (priceWrapper && !priceWrapper.querySelector('[data-avid-app-pricing-review]')) {
        priceWrapper.textContent = '';
        const message = document.createElement('span');
        message.className = 'spf-product-card__pricing-review';
        message.dataset.avidAppPricingReview = '';
        message.setAttribute('role', 'status');
        message.textContent = 'Contact AVID for pricing';
        priceWrapper.append(message);
      }
    };

    const scan = (root = document) => {
      if (root instanceof Element && root.matches('.spf-product-card')) guardCard(root);
      root.querySelectorAll?.('.spf-product-card').forEach(guardCard);
    };

    scan();
    if (document.documentElement.dataset.avidThirdPartyPriceGuardReady === 'true') return;
    document.documentElement.dataset.avidThirdPartyPriceGuardReady = 'true';

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        const inspectNode = (node) => {
          if (!(node instanceof Element)) return;
          const card = node.matches('.spf-product-card') ? node : node.closest('.spf-product-card');
          if (card) guardCard(card);
          scan(node);
        };
        if (mutation.type === 'attributes') inspectNode(mutation.target);
        else mutation.addedNodes.forEach(inspectNode);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-price', 'data-available'] });

    document.addEventListener('click', (event) => {
      const card = event.target.closest?.('.spf-product-card');
      if (!card || !thirdPartyCardRequiresPricingReview(card)) return;
      const blockedControl = event.target.closest('.open-quick-view, .spf-product__form button, .spf-product__form [type="submit"]');
      if (!blockedControl) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      guardCard(card, { failClosed: true });
      this.announce('This product requires an AVID pricing review before purchase.');
    }, true);

    document.addEventListener('submit', (event) => {
      const card = event.target.closest?.('.spf-product-card');
      if (!card || !thirdPartyCardRequiresPricingReview(card)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      guardCard(card, { failClosed: true });
      this.announce('This product requires an AVID pricing review before purchase.');
    }, true);
  }
}

let activeAvidTheme = null;
const startAvidTheme = () => {
  activeAvidTheme = new AvidTheme();
  return activeAvidTheme;
};

globalThis.AvidDealerPricing = {
  createPriceElement: createStorefrontPriceElement,
  hydrate: (root = document) => activeAvidTheme?.hydrateDealerPrices(root),
  async pricesFor(variantIds) {
    // Reuse the signed proxy and its validation; detached rows never render private prices.
    const container = document.createElement('div');
    const price = document.createElement('span');
    price.dataset.dealerPricing = '';
    price.dataset.dealerVariantIds = [...new Set(variantIds)].join(',');
    container.append(price);
    await activeAvidTheme?.hydrateDealerPrices(container);
    return new Map(variantIds.flatMap(id => dealerPricingByVariant.has(id) ? [[id, dealerPricingByVariant.get(id)]] : []));
  }
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startAvidTheme);
else startAvidTheme();

document.addEventListener('shopify:section:load', startAvidTheme);
